import { useEffect, useState } from "react";
import { Card, Spinner } from "react-bootstrap";
import { useLanguage } from "../../../i18n/LanguageProvider";
import { MarketSignal, useApi } from "../apiService";
import { fmtTime, polarityClass, SourceChips, SourceModal, useSourceModal } from "./signalsShared";
import { Calendar, MapPin, TrendDown, TrendFlat, TrendUp } from "../../icons";
import "./signals.css";

type Api = ReturnType<typeof useApi>;

// On mobile the left column isn't height-capped (no internal scroll), so the
// full list would run very long. Collapse to this many and offer "show more".
const MOBILE_COLLAPSED_COUNT = 4;

export const SignalsList = ({ api }: { api: Api }) => {
  const { t, language } = useLanguage();
  const [signals, setSignals] = useState<MarketSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const { activeGroup, setActiveGroup } = useSourceModal();

  // Mobile (below the lg breakpoint where columns sit side by side). Desktop
  // keeps the internal-scroll card, so the collapse only applies on mobile.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 991.98px)").matches,
  );
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 991.98px)");
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .fetchSignals(20)
      .then((s) => {
        if (cancelled) return;
        setSignals(s.signals || []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSignals([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const polarityLabel = (p: number): string =>
    p > 0.1 ? t("signals.bullish") : p < -0.1 ? t("signals.bearish") : t("signals.neutral");

  const collapsed = isMobile && !expanded;
  const visibleSignals = collapsed ? signals.slice(0, MOBILE_COLLAPSED_COUNT) : signals;
  const hiddenCount = signals.length - MOBILE_COLLAPSED_COUNT;

  return (
    <Card className="shadow-card h-100">
      <Card.Body>
        <h5 className="signals-title">{t("signals.title")}</h5>

        {loading && (
          <div className="signals-loading">
            <Spinner animation="border" size="sm" /> {t("signals.loading")}
          </div>
        )}

        {!loading && signals.length === 0 && (
          <div className="signals-empty">{t("signals.empty")}</div>
        )}

        {!loading && signals.length > 0 && (
          <ul className="signals-list">
            {visibleSignals.map((s, i) => {
              const dir = polarityClass(s.polarity);
              return (
                <li key={`${s.createdAt}-${i}`} className="signal-item">
                  <span className={`signal-dir ${dir}`} title={polarityLabel(s.polarity)}>
                    {dir === "up" ? (
                      <TrendUp title={polarityLabel(s.polarity)} />
                    ) : dir === "down" ? (
                      <TrendDown title={polarityLabel(s.polarity)} />
                    ) : (
                      <TrendFlat title={polarityLabel(s.polarity)} />
                    )}
                  </span>
                  <div className="signal-body">
                    <p className="signal-summary">
                      {language === "sv" ? s.summarySv : s.summaryEn}
                    </p>
                    <div className="signal-meta">
                      {s.locationName && (
                        <span className="signal-loc">
                          <MapPin className="signal-meta-icon" /> {s.locationName}
                        </span>
                      )}
                      {/* Event time only when the signal carries a concrete time;
                          otherwise just the publish time, so the two aren't
                          confused for one another. */}
                      {s.eventTime && (
                        <span className="signal-time signal-time--event">
                          <Calendar className="signal-meta-icon" /> {t("signals.event")}:{" "}
                          {fmtTime(s.eventTime)}
                        </span>
                      )}
                      <span className="signal-time signal-time--pub">
                        {t("signals.published")}: {fmtTime(s.createdAt)}
                      </span>
                    </div>
                    <SourceChips sources={s.sources} onPick={setActiveGroup} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {!loading && isMobile && hiddenCount > 0 && (
          <button
            type="button"
            className="signals-more-btn"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded
              ? t("signals.showLess")
              : `${t("signals.showMore")} (${hiddenCount})`}
          </button>
        )}
      </Card.Body>
      <SourceModal group={activeGroup} onClose={() => setActiveGroup(null)} />
    </Card>
  );
};
